import { createServerFn } from "@tanstack/react-start";
import { notFound } from "@tanstack/react-router";
import { getRequestHeader, getRequestIP, setResponseHeader } from "@tanstack/react-start/server";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

// Allowlisted admin action types — restricts what can be written to the audit log
const ADMIN_ACTION = z.enum([
  "console.view",
  "console.login",
  "console.logout",
  "user.suspend",
  "user.unsuspend",
  "user.role_grant",
  "user.role_revoke",
  "listing.remove",
  "listing.restore",
  "order.cancel",
  "order.refund",
  "dispute.open",
  "dispute.resolve",
  "rating.remove",
  "message.remove",
  "config.update",
  "audit.export",
]);

const LogAdminActionSchema = z.object({
  action: ADMIN_ACTION,
  targetType: z.string().trim().max(64).optional(),
  targetId: z.string().trim().max(128).optional(),
  metadata: z
    .record(z.string().max(128), z.unknown())
    .refine((m) => JSON.stringify(m).length <= 4096, {
      message: "metadata exceeds 4KB",
    })
    .optional(),
});

// 2-hour admin session inactivity window
const ADMIN_INACTIVITY_MS = 2 * 60 * 60 * 1000;

type AdminStatus =
  | { ok: true; needs2fa: boolean; lastActivityAt: string | null }
  | never;

/**
 * Verifies the current user is an admin AND has fresh 2FA + active session.
 * On any failure throws notFound() — the route loader surfaces a 404 with no
 * hint that the panel exists.
 *
 * Also writes no-store/noindex headers so the operations console is never
 * cached by browsers, proxies, or search engines.
 */
export const verifyAdminAccess = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<AdminStatus> => {
    const { supabase, userId } = context;

    // 1. Role check via RLS-safe security-definer helper
    const { data: isAdmin, error: roleErr } = await supabase.rpc("has_role", {
      _user_id: userId,
      _role: "admin",
    });
    if (roleErr || !isAdmin) throw notFound();

    // 2. Security headers — no caching, no indexing
    setResponseHeader("Cache-Control", "no-store, no-cache, must-revalidate, private");
    setResponseHeader("Pragma", "no-cache");
    setResponseHeader("X-Robots-Tag", "noindex, nofollow, noarchive, nosnippet");
    setResponseHeader(
      "Strict-Transport-Security",
      "max-age=63072000; includeSubDomains; preload",
    );

    // 3. Session freshness + 2FA enrollment
    const { data: sec } = await supabase
      .from("admin_security")
      .select("totp_enrolled, last_activity_at, last_2fa_verified_at")
      .eq("user_id", userId)
      .maybeSingle();

    const lastActivityAt = sec?.last_activity_at ?? null;
    if (lastActivityAt) {
      const idleMs = Date.now() - new Date(lastActivityAt).getTime();
      if (idleMs > ADMIN_INACTIVITY_MS) {
        // Session expired — force re-auth + 2FA
        throw notFound();
      }
    }

    // Touch activity timestamp (best-effort)
    await supabase
      .from("admin_security")
      .upsert(
        { user_id: userId, last_activity_at: new Date().toISOString() },
        { onConflict: "user_id" },
      );

    // 4. Audit the page view
    const ip = getRequestIP({ xForwardedFor: true }) ?? null;
    const ua = getRequestHeader("user-agent") ?? null;
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    await supabaseAdmin.from("admin_audit_log").insert({
      admin_user_id: userId,
      action: "console.view",
      ip_address: ip,
      user_agent: ua,
    });

    return {
      ok: true,
      needs2fa: !sec?.totp_enrolled,
      lastActivityAt,
    };
  });

/**
 * Records an admin action in the tamper-resistant audit log.
 * Called from inside the operations console for every privileged action.
 */
export const logAdminAction = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => LogAdminActionSchema.parse(input))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: isAdmin } = await supabase.rpc("has_role", {
      _user_id: userId,
      _role: "admin",
    });
    if (!isAdmin) throw notFound();

    const ip = getRequestIP({ xForwardedFor: true }) ?? null;
    const ua = getRequestHeader("user-agent") ?? null;
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    await supabaseAdmin.from("admin_audit_log").insert({
      admin_user_id: userId,
      action: data.action,
      target_type: data.targetType ?? null,
      target_id: data.targetId ?? null,
      metadata: (data.metadata ?? {}) as never,
      ip_address: ip,
      user_agent: ua,
    });
    return { ok: true };
  });
